import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const sections = [
  { id: 'strategy', icon: '🧭' },
  { id: 'foundation', icon: '🏗️' },
  { id: 'architecture', icon: '🧠' },
  { id: 'rulepacks', icon: '📦' },
  { id: 'ruleDocs', icon: '📝' },
  { id: 'ruleCatalog', icon: '📚' },
  { id: 'api', icon: '🔌' },
  { id: 'builtinRules', icon: '🛡️' },
  { id: 'contributors', icon: '🧑‍💻' },
  { id: 'temporary', icon: '⚠️' },
  { id: 'next', icon: '🚀' },
];

function SectionHeading({ icon, title }) {
  return (
    <h2 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span>{icon}</span> {title}
    </h2>
  );
}

function BulletList({ items }) {
  return (
    <ul style={{ lineHeight: 1.8, paddingLeft: '1.25rem', marginBottom: 0 }}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function TermPills({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.75rem' }}>
      {items.map((item) => (
        <span
          key={item}
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            padding: '0.2rem 0.6rem',
            borderRadius: '100px',
            background: 'var(--bs-tertiary-bg)',
            color: 'var(--bs-secondary-color)',
            border: '1px solid var(--bs-border-color)',
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function RuleCatalog({ t }) {
  const items = t('intelligenceDocs.ruleCatalog.items', { returnObjects: true });

  return (
    <div className="row g-3 mt-1">
      {items.map((item) => (
        <div key={item.id} className="col-12 col-xl-6">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-3 flex-wrap align-items-start mb-2">
                <div>
                  <div className="fw-semibold">{item.name}</div>
                  <div className="small font-monospace text-secondary">{item.id}</div>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <span className="badge bg-light text-dark border">{item.cwe}</span>
                  <span className="badge bg-light text-dark border">{item.owasp}</span>
                </div>
              </div>
              <div className="small mb-3" style={{ lineHeight: 1.7 }}>{item.summary}</div>
              <div className="small text-secondary" style={{ lineHeight: 1.7 }}>
                <strong>{t('intelligenceDocs.ruleCatalog.fields.flow')}</strong> {item.flow}
              </div>
              <div className="small text-secondary mt-2" style={{ lineHeight: 1.7 }}>
                <strong>{t('intelligenceDocs.ruleCatalog.fields.safe')}</strong> {item.safe}
              </div>
              <div className="small text-secondary mt-2" style={{ lineHeight: 1.7 }}>
                <strong>{t('intelligenceDocs.ruleCatalog.fields.runtime')}</strong> {item.runtime}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function IntelligenceDocs() {
  const { t } = useTranslation();

  return (
    <div className="App-body">
      <div className="ce-page-header" style={{ paddingLeft: 0, paddingRight: 0, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 className="ce-page-title">🧠 {t('intelligenceDocs.title')}</h1>
            <p className="ce-page-subtitle">{t('intelligenceDocs.subtitle')}</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Link className="btn btn-outline-secondary" to="/docs/intelligence/rule-roadmap">
              {t('intelligenceDocs.ruleRoadmapAction')}
            </Link>
            <Link className="btn btn-outline-primary" to="/docs">
              {t('intelligenceDocs.backToDocs')}
            </Link>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '2.5rem', alignItems: 'start' }}>
        <div className="docs-layout-toc" style={{ position: 'sticky', top: '1.5rem' }}>
          <div className="section-title" style={{ marginBottom: '0.625rem' }}>{t('intelligenceDocs.contents')}</div>
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                display: 'block',
                padding: '0.3rem 0.625rem',
                borderRadius: 'var(--radius-xs)',
                fontSize: '0.8125rem',
                color: 'var(--bs-secondary-color)',
                textDecoration: 'none',
                marginBottom: '1px',
              }}
            >
              {section.icon} {t(`intelligenceDocs.sections.${section.id}.title`)}
            </a>
          ))}
        </div>

        <div className="docs-layout-content">
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(59,130,246,0.08))',
              border: '1px solid var(--bs-border-color)',
              borderRadius: 14,
              padding: '1.25rem 1.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--bs-secondary-color)' }}>
              {t('intelligenceDocs.heroLabel')}
            </div>
            <p style={{ margin: '0.75rem 0 0', lineHeight: 1.75 }}>
              {t('intelligenceDocs.heroBody')}
            </p>
            <TermPills items={t('intelligenceDocs.heroPills', { returnObjects: true })} />
          </div>

          {sections.map((section) => (
            <section key={section.id} id={section.id} style={{ marginBottom: '2.5rem', scrollMarginTop: '1.5rem' }}>
              <SectionHeading icon={section.icon} title={t(`intelligenceDocs.sections.${section.id}.title`)} />
              <p style={{ lineHeight: 1.75 }}>{t(`intelligenceDocs.sections.${section.id}.body`)}</p>
              <BulletList items={t(`intelligenceDocs.sections.${section.id}.bullets`, { returnObjects: true })} />
              {section.id === 'ruleCatalog' && <RuleCatalog t={t} />}
            </section>
          ))}

          <div
            style={{
              background: 'var(--bs-tertiary-bg)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: 12,
              padding: '1rem 1.25rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.375rem' }}>{t('intelligenceDocs.relatedTitle')}</div>
            <div style={{ lineHeight: 1.7 }}>
              {t('intelligenceDocs.relatedBody')}{' '}
              <code>P20604-code-explorer/docs/intelligence.md</code>{' '}
              {t('intelligenceDocs.relatedAnd')}{' '}
              <code>P20604-code-explorer/docs/intelligence-rules.md</code>.{' '}
              <Link to="/docs/intelligence/rule-roadmap">{t('intelligenceDocs.relatedRoadmapLink')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
